/**
 * Growth Portal public runtime / build-time defaults.
 * `NEXT_PUBLIC_CDN_STATIC_PREFIX` stays in app `.env` (not defined here).
 *
 * Explicit `process.env` values (e.g. from `.env.local`) take precedence when set.
 */

type GrowthPortalClientEnv = string;

type GrowthPortalPublicConfig = {
  ossBucket: string;
  cdnBaseUrl: string;
  gaId: string;
  bugsnagApiKey: string;
  /** Dashboard 独立部署源站（不含尾斜杠），用于 `/dashboard` rewrite。 */
  dashboardVercelOrigin: string;
  thirdwebClientId: string;
  thirdwebSecretKey?: string;
};

const SHARED: Pick<
  GrowthPortalPublicConfig,
  "thirdwebClientId" | "thirdwebSecretKey"
> = {
  thirdwebClientId: "398ccbd07faea0b0af784fd8b740a59d",
  thirdwebSecretKey: process.env.NEXT_PUBLIC_THIRDWEB_SECRET_KEY,
};

const NON_PROD: Pick<
  GrowthPortalPublicConfig,
  | "ossBucket"
  | "cdnBaseUrl"
  | "gaId"
  | "bugsnagApiKey"
  | "dashboardVercelOrigin"
> = {
  ossBucket: "trex-static-dev",
  cdnBaseUrl: "https://static-dev.trex.xyz",
  gaId: "G-0YZQ0V5GTT",
  bugsnagApiKey: "700298260ab42b50d79f61b2a429bfb1",
  dashboardVercelOrigin: "https://trex-2b-preview-dashboard.vercel.app",
};

const PROD: Pick<
  GrowthPortalPublicConfig,
  | "ossBucket"
  | "cdnBaseUrl"
  | "gaId"
  | "bugsnagApiKey"
  | "dashboardVercelOrigin"
> = {
  ossBucket: "trex-static-prod",
  cdnBaseUrl: "https://static.trex.xyz",
  gaId: "G-BEYZWT6P8Z",
  bugsnagApiKey: "c2fa7eab4120dd60f35875fdfff03033",
  dashboardVercelOrigin: "https://trex-2b-dashboard.vercel.app",
};

export function resolveGrowthPortalClientEnv(
  raw?: string | null,
): GrowthPortalClientEnv {
  const v = (raw ?? "").trim();
  return v || "dev";
}

export function getGrowthPortalPublicConfig(
  clientEnv: GrowthPortalClientEnv,
): GrowthPortalPublicConfig {
  const envSpecific = clientEnv === "prod" ? PROD : NON_PROD;
  return { ...SHARED, ...envSpecific };
}

/** Keys aligned with former `NEXT_PUBLIC_*` usage across next-kit, utils, bugsnag, thirdweb. */
export function ensureGrowthPortalProcessEnv(): void {
  if (typeof process === "undefined") return;

  const clientEnv = resolveGrowthPortalClientEnv(
    process.env.NEXT_PUBLIC_CLIENT_ENV,
  );
  const cfg = getGrowthPortalPublicConfig(clientEnv);

  const entries: Record<string, string> = {
    NEXT_PUBLIC_CDN_URL: cfg.cdnBaseUrl,
    OSS_BUCKET: cfg.ossBucket,
    NEXT_PUBLIC_THIRDWEB_CLIENT_ID: cfg.thirdwebClientId,
    NEXT_PUBLIC_GA_ID: cfg.gaId,
    NEXT_PUBLIC_BUGSNAG_API_KEY: cfg.bugsnagApiKey,
    DASHBOARD_VERCEL_ORIGIN: cfg.dashboardVercelOrigin,
  };

  for (const [key, value] of Object.entries(entries)) {
    if (!process.env[key]?.trim()) {
      process.env[key] = value;
    }
  }
}
